import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Download, Trash2, Upload, Save, Edit3, Check, X, Plus, Users, ChevronLeft, ChevronRight, Star, Home } from 'lucide-react';
import api from '../utils/api';
import SignatureTools from '../components/SignatureTools';
import ProgressGauge from '../components/ProgressGauge';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const PARTNER_TYPES = [
  { value: 'SYNDIC', label: 'Syndic / Maître d\'ouvrage' },
  { value: 'MAITRE_OEUVRE', label: 'Maître d\'œuvre' },
  { value: 'BUREAU_CONTROLE', label: 'Bureau de contrôle' },
  { value: 'COORDINATEUR_SPS', label: 'Coordinateur SPS' },
  { value: 'ENTREPRISE', label: 'Entreprise' },
  { value: 'OTHER', label: 'Autre' },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [form, setForm] = useState({});
  const [qr, setQr] = useState(null);
  const [updateComment, setUpdateComment] = useState('');
  const [tab, setTab] = useState('info');
  const [uploading, setUploading] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  const [editingCaption, setEditingCaption] = useState(null);
  const [editCaptionText, setEditCaptionText] = useState('');
  const [savingProgress, setSavingProgress] = useState(false);

  // Ordre des photos : l'affichage est mis à jour au clic, l'enregistrement
  // suit. Un seul appel en vol à la fois, et c'est toujours le dernier ordre
  // connu qui part — sinon deux clics rapides peuvent arriver dans le désordre
  // au serveur et enregistrer un ordre que l'utilisateur n'a jamais vu.
  const ordreCible = useRef(null);
  const envoiOrdreEnCours = useRef(false);

  // Intervenants state
  const [allPartners, setAllPartners] = useState([]);
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [lotLabel, setLotLabel] = useState('');
  const [editingLink, setEditingLink] = useState(null);
  const [editLotLabel, setEditLotLabel] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      setForm({
        name: data.name, dossierNumber: data.dossierNumber || '', permitNumber: data.permitNumber || '',
        description: data.description || '', address: data.address || '',
        clientName: data.clientName || '', startDate: data.startDate?.slice(0, 10) || '',
        endDate: data.endDate?.slice(0, 10) || '',
        publicAccessEnabled: data.publicAccessEnabled,
        showPhotos: data.showPhotos, showComments: data.showComments,
        showDates: data.showDates, showProgress: data.showProgress,
        duration: data.duration || '',
        dpNumber: data.dpNumber || '',
        dpDate: data.dpDate || '',
        residenceName: data.residenceName || '',
      });
    } catch {
      toast.error('Chantier introuvable');
      navigate('/dashboard');
    }
  }, [id, navigate]);

  const loadPartners = useCallback(async () => {
    try {
      const { data } = await api.get('/partners');
      setAllPartners(data);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => { load(); loadPartners(); }, [load, loadPartners]);

  const save = async () => {
    try {
      await api.put(`/projects/${id}`, { ...form, comment: updateComment || undefined });
      setUpdateComment('');
      load();
      toast.success('Sauvegardé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  // Enregistrement immédiat : la jauge est le geste courant du chef de chantier,
  // il ne doit pas avoir à chercher un bouton « Sauvegarder ».
  const saveProgress = async (progress) => {
    setSavingProgress(true);
    try {
      await api.put(`/projects/${id}`, { progress });
      await load();
      toast.success(`Avancement : ${progress} %`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
      load(); // réaffiche la valeur réellement enregistrée
    } finally {
      setSavingProgress(false);
    }
  };

  const toggleCompleted = async () => {
    const newStatus = project.status === 'COMPLETED' ? 'IN_PROGRESS' : 'COMPLETED';
    try {
      await api.put(`/projects/${id}`, { status: newStatus });
      load();
      toast.success(newStatus === 'COMPLETED' ? 'Chantier marqué comme terminé' : 'Chantier remis en cours');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const deleteProject = async () => {
    if (!window.confirm('Supprimer ce chantier ?')) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Chantier supprimé');
      navigate('/dashboard');
    } catch {
      toast.error('Erreur de suppression');
    }
  };

  const loadQR = async () => {
    try {
      const { data } = await api.get(`/projects/${id}/qrcode`);
      setQr(data);
    } catch {
      toast.error('Erreur QR code');
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('photo', file);
    if (photoCaption.trim()) fd.append('caption', photoCaption.trim());
    try {
      await api.post(`/projects/${id}/photos`, fd);
      setPhotoCaption('');
      load();
      toast.success('Photo ajoutée');
    } catch {
      toast.error('Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const saveCaption = async (photoId) => {
    try {
      await api.patch(`/projects/photos/${photoId}`, { caption: editCaptionText });
      setEditingCaption(null);
      load();
      toast.success('Description mise à jour');
    } catch {
      toast.error('Erreur');
    }
  };

  const pousserOrdrePhotos = useCallback(async () => {
    if (envoiOrdreEnCours.current) return; // l'envoi en cours reprendra l'ordre le plus récent
    envoiOrdreEnCours.current = true;
    try {
      while (ordreCible.current) {
        const photoIds = ordreCible.current;
        ordreCible.current = null;
        await api.patch(`/projects/${id}/photos/reorder`, { photoIds });
      }
    } catch {
      ordreCible.current = null;
      toast.error('Impossible d\'enregistrer l\'ordre des photos');
      load(); // revenir à l'ordre réellement enregistré
    } finally {
      envoiOrdreEnCours.current = false;
    }
  }, [id, load]);

  const movePhoto = (index, delta) => {
    const cible = index + delta;
    if (!project || cible < 0 || cible >= project.photos.length) return;

    const photos = [...project.photos];
    [photos[index], photos[cible]] = [photos[cible], photos[index]];

    setProject(p => ({ ...p, photos }));
    ordreCible.current = photos.map(photo => photo.id);
    pousserOrdrePhotos();
  };

  // Photo de couverture : vignette du chantier dans les listes, l'accueil et
  // l'aperçu de lien. Enregistrée aussitôt, comme l'ordre des photos.
  const setCover = async (photoId) => {
    try {
      await api.put(`/projects/${id}`, { coverPhotoId: photoId });
      await load();
      toast.success(photoId ? 'Photo de couverture définie' : 'Couverture : première photo');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  // Chantier d'accueil : un seul par entreprise, celui-ci remplace le précédent.
  const setFeatured = async (featured) => {
    try {
      await api.put(`/projects/${id}`, { featured });
      await load();
      toast.success(featured ? 'Chantier mis en avant sur l\'accueil' : 'Chantier retiré de l\'accueil');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await api.delete(`/projects/photos/${photoId}`);
      load();
      toast.success('Photo supprimée');
    } catch {
      toast.error('Erreur');
    }
  };

  // Intervenants
  const addPartner = async () => {
    if (!selectedPartnerId) { toast.error('Sélectionnez un intervenant'); return; }
    try {
      await api.post(`/projects/${id}/partners`, { partnerId: selectedPartnerId, lotLabel: lotLabel || undefined });
      setShowAddPartner(false);
      setSelectedPartnerId('');
      setLotLabel('');
      load();
      toast.success('Intervenant ajouté');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const removePartner = async (partnerId) => {
    try {
      await api.delete(`/projects/${id}/partners/${partnerId}`);
      load();
      toast.success('Intervenant retiré');
    } catch {
      toast.error('Erreur');
    }
  };

  const saveLotLabel = async (partnerId) => {
    try {
      await api.put(`/projects/${id}/partners/${partnerId}`, { lotLabel: editLotLabel });
      setEditingLink(null);
      load();
      toast.success('Mis à jour');
    } catch {
      toast.error('Erreur');
    }
  };

  if (!project) return <div className="container" style={{ padding: 40, textAlign: 'center' }}>Chargement...</div>;

  const linkedPartnerIds = new Set((project.partners || []).map(pp => pp.partnerId));
  const availablePartners = allPartners.filter(p => !linkedPartnerIds.has(p.id));

  const tabs = [
    { key: 'info', label: 'Informations' },
    { key: 'intervenants', label: `Intervenants (${(project.partners || []).length})` },
    { key: 'photos', label: `Photos (${project.photos.length})` },
    { key: 'history', label: 'Historique' },
    { key: 'qrcode', label: 'QR Code' },
    { key: 'suivi', label: 'Suivi & rapports' },
    { key: 'settings', label: 'Paramètres' },
  ];

  return (
    <div className="container" style={{ padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn" onClick={() => navigate('/dashboard')}><ArrowLeft size={16} /> Retour</button>
        <button className="btn btn-danger btn-sm" onClick={deleteProject}><Trash2 size={14} /> Supprimer</button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>{project.name}</h2>
        <ProgressGauge value={project.progress} onCommit={saveProgress} saving={savingProgress} />
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.key} className={`btn btn-sm ${tab === t.key ? 'btn-primary' : ''}`}
            onClick={() => { setTab(t.key); if (t.key === 'qrcode') loadQR(); }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <div className="card">
          <div className="form-row">
            <div className="form-group">
              <label>Nom du chantier</label>
              <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>N° de dossier</label>
              <input value={form.dossierNumber || ''} onChange={e => setForm({ ...form, dossierNumber: e.target.value })} placeholder="Ex: 2024-001" />
            </div>
          </div>
          <div className="form-group">
            <label>Nom de la résidence</label>
            <input value={form.residenceName || ''} onChange={e => setForm({ ...form, residenceName: e.target.value })}
              placeholder="Ex: Résidence ROBERT SCHUMANN" />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Adresse</label>
            <input value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Durée des travaux</label>
              <input value={form.duration || ''} onChange={e => setForm({ ...form, duration: e.target.value })}
                placeholder="Ex: 15 mois" />
            </div>
            <div className="form-group">
              <label>N° de permis (DP)</label>
              <input value={form.dpNumber || ''} onChange={e => setForm({ ...form, dpNumber: e.target.value })}
                placeholder="Ex: DP n° 92 040 23 0044" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Date de délivrance du permis</label>
              <input value={form.dpDate || ''} onChange={e => setForm({ ...form, dpDate: e.target.value })}
                placeholder="Ex: 03/07/2023" />
            </div>
            <div className="form-group" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Date de début</label>
              <input type="date" value={form.startDate || ''} onChange={e => setForm({ ...form, startDate: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Date de fin prévue</label>
              <input type="date" value={form.endDate || ''} onChange={e => setForm({ ...form, endDate: e.target.value })} />
            </div>
          </div>
          <button className="btn btn-primary" onClick={save}><Save size={16} /> Sauvegarder</button>
        </div>
      )}

      {tab === 'intervenants' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0 }}>Intervenants du chantier</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: 4 }}>
                Ces informations apparaîtront sur la page publique (QR code).
              </p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddPartner(!showAddPartner)}>
              <Plus size={14} /> Ajouter
            </button>
          </div>

          {showAddPartner && (
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid var(--border)' }}>
              <h4 style={{ marginBottom: 12, fontSize: '0.9rem' }}>Ajouter un intervenant</h4>
              {availablePartners.length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
                  Tous vos intervenants sont déjà ajoutés, ou vous n'en avez pas encore créé.{' '}
                  <Link to="/partners" style={{ color: 'var(--primary)' }}>Gérer les intervenants →</Link>
                </div>
              ) : (
                <>
                  <div className="form-row" style={{ marginBottom: 8 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.85rem' }}>Intervenant</label>
                      <select value={selectedPartnerId} onChange={e => setSelectedPartnerId(e.target.value)}>
                        <option value="">— Sélectionner —</option>
                        {PARTNER_TYPES.map(t => {
                          const items = availablePartners.filter(p => p.type === t.value);
                          if (!items.length) return null;
                          return (
                            <optgroup key={t.value} label={t.label}>
                              {items.map(p => <option key={p.id} value={p.id}>{p.name}{p.contactName ? ` (${p.contactName})` : ''}</option>)}
                            </optgroup>
                          );
                        })}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.85rem' }}>Lot / Rôle spécifique (optionnel)</label>
                      <input value={lotLabel} onChange={e => setLotLabel(e.target.value)}
                        placeholder="Ex: Lot 01 : Ravalement" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={addPartner}><Check size={14} /> Confirmer</button>
                    <button className="btn btn-sm" onClick={() => { setShowAddPartner(false); setSelectedPartnerId(''); setLotLabel(''); }}>
                      <X size={14} /> Annuler
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {(project.partners || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-light)' }}>
              <Users size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p>Aucun intervenant associé à ce chantier.</p>
              <p style={{ fontSize: '0.85rem' }}>
                Ajoutez des intervenants depuis votre{' '}
                <Link to="/partners" style={{ color: 'var(--primary)' }}>carnet d'adresses</Link>.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PARTNER_TYPES.map(t => {
                const items = (project.partners || []).filter(pp => pp.partner.type === t.value);
                if (!items.length) return null;
                return (
                  <div key={t.value}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                      {t.label}
                    </div>
                    {items.map(pp => (
                      <div key={pp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#f8f9fa', borderRadius: 8, marginBottom: 4 }}>
                        {pp.partner.logo && (
                          <img src={`${API_BASE}/uploads/${pp.partner.logo}`} alt={pp.partner.name}
                            style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, background: 'white', padding: 2 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{pp.partner.name}</div>
                          {pp.partner.contactName && (
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                              {pp.partner.contactName}{pp.partner.contactTitle ? ` — ${pp.partner.contactTitle}` : ''}
                            </div>
                          )}
                          {editingLink === pp.partnerId ? (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 4 }}>
                              <input value={editLotLabel} onChange={e => setEditLotLabel(e.target.value)}
                                style={{ flex: 1, padding: '3px 8px', fontSize: '0.8rem', border: '1px solid var(--border)', borderRadius: 4 }}
                                placeholder="Lot / Rôle..." autoFocus />
                              <button className="btn btn-sm btn-primary" style={{ padding: '3px 6px' }} onClick={() => saveLotLabel(pp.partnerId)}>
                                <Check size={12} />
                              </button>
                              <button className="btn btn-sm" style={{ padding: '3px 6px' }} onClick={() => setEditingLink(null)}>
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 2, cursor: 'pointer' }}
                              onClick={() => { setEditingLink(pp.partnerId); setEditLotLabel(pp.lotLabel || ''); }}>
                              <span style={{ fontSize: '0.8rem', color: pp.lotLabel ? 'var(--text)' : 'var(--border)', fontStyle: pp.lotLabel ? 'normal' : 'italic' }}>
                                {pp.lotLabel || 'Ajouter un lot / rôle…'}
                              </span>
                              <Edit3 size={11} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
                            </div>
                          )}
                        </div>
                        <button className="btn btn-sm btn-danger" style={{ padding: '4px 8px' }} onClick={() => removePartner(pp.partnerId)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'photos' && (
        <div className="card">
          <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 250px' }}>
              <label>Description (optionnel)</label>
              <input value={photoCaption} onChange={e => setPhotoCaption(e.target.value)}
                placeholder="Ex: Fondations terminées, vue côté rue..." />
            </div>
            <label className="btn btn-primary" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <Upload size={16} /> {uploading ? 'Envoi...' : 'Ajouter une photo'}
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadPhoto} disabled={uploading} />
            </label>
          </div>
          {project.photos.length === 0 ? (
            <p style={{ color: 'var(--text-light)' }}>Aucune photo pour ce chantier.</p>
          ) : (
            <>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: 12 }}>
                Réordonnez avec les flèches. La photo marquée <Star size={12} style={{ display: 'inline', verticalAlign: -1 }} /> sert de
                vignette au chantier (listes, accueil, aperçu de lien) ; sans choix, c'est la photo n° 1.
              </p>
              <div className="photos-grid">
                {project.photos.map((photo, index) => (
                  <div key={photo.id}>
                    {/* Commandes superposées à la vignette : sur mobile les cases
                        descendent à 100px, et des boutons en flux y déborderaient
                        sur la photo voisine. */}
                    <div className="photo-thumb">
                      <img src={`${API_BASE}/uploads/${photo.filename}`} alt={photo.caption || 'Photo chantier'} />
                      <span className="photo-rank" title={`Position ${index + 1} sur ${project.photos.length}`}>
                        {index + 1}
                      </span>
                      <div className="photo-move">
                        <button type="button" disabled={index === 0}
                          title="Déplacer vers la gauche"
                          aria-label={`Déplacer la photo ${index + 1} vers la gauche`}
                          onClick={() => movePhoto(index, -1)}>
                          <ChevronLeft size={14} />
                        </button>
                        <button type="button" disabled={index === project.photos.length - 1}
                          title="Déplacer vers la droite"
                          aria-label={`Déplacer la photo ${index + 1} vers la droite`}
                          onClick={() => movePhoto(index, 1)}>
                          <ChevronRight size={14} />
                        </button>
                      </div>
                      <button type="button" className="photo-delete"
                        title="Supprimer la photo"
                        aria-label={`Supprimer la photo ${index + 1}`}
                        onClick={() => deletePhoto(photo.id)}>
                        <Trash2 size={13} />
                      </button>
                      <button type="button"
                        className={`photo-cover ${project.coverPhotoId === photo.id ? 'is-cover' : ''}`}
                        title={project.coverPhotoId === photo.id ? 'Photo de couverture (cliquer pour revenir à la photo n° 1)' : 'Définir comme photo de couverture'}
                        aria-label={project.coverPhotoId === photo.id ? `Photo ${index + 1} : couverture` : `Définir la photo ${index + 1} comme couverture`}
                        aria-pressed={project.coverPhotoId === photo.id}
                        onClick={() => setCover(project.coverPhotoId === photo.id ? null : photo.id)}>
                        <Star size={13} /> {project.coverPhotoId === photo.id ? 'Couverture' : ''}
                      </button>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      {editingCaption === photo.id ? (
                        // Champ sur sa propre ligne, boutons dessous : sur une seule
                        // ligne, la largeur minimale de l'input poussait « valider »
                        // hors de la case, sous la photo suivante — qui captait le
                        // clic et faisait perdre la description saisie.
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          <input value={editCaptionText} onChange={e => setEditCaptionText(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveCaption(photo.id);
                              if (e.key === 'Escape') setEditingCaption(null);
                            }}
                            style={{ flex: '1 1 100%', minWidth: 0, padding: '4px 8px', fontSize: '0.8rem', border: '1px solid var(--border)', borderRadius: 4 }}
                            placeholder="Description..." autoFocus />
                          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', flexShrink: 0 }}>
                            <button className="btn btn-sm btn-primary" style={{ padding: '4px 6px' }}
                              title="Enregistrer la description"
                              onClick={() => saveCaption(photo.id)}><Check size={12} /></button>
                            <button className="btn btn-sm" style={{ padding: '4px 6px' }}
                              title="Annuler"
                              onClick={() => setEditingCaption(null)}><X size={12} /></button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}
                          onClick={() => { setEditingCaption(photo.id); setEditCaptionText(photo.caption || ''); }}>
                          <p style={{
                            fontSize: '0.8rem', flex: 1, minWidth: 0,
                            // var(--border) était illisible sur fond blanc : l'invite
                            // « Ajouter une description » passait inaperçue.
                            color: 'var(--text-light)',
                            fontStyle: photo.caption ? 'normal' : 'italic',
                          }}>
                            {photo.caption || 'Ajouter une description...'}
                          </p>
                          <Edit3 size={12} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>Activité du chantier</h3>
          {project.updates.length === 0 ? (
            <p style={{ color: 'var(--text-light)' }}>Aucune activité enregistrée.</p>
          ) : (
            <ul className="timeline">
              {project.updates.map(u => (
                <li key={u.id} className="timeline-item">
                  <span className="timeline-meta">
                    {new Date(u.createdAt).toLocaleString('fr-FR')} — {u.user.name}
                  </span>
                  {u.comment && <p style={{ marginTop: 4, marginBottom: 0 }}>{u.comment}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === 'qrcode' && (
        <div className="card qr-section">
          <h3>QR Code du chantier</h3>
          <p style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
            Scannez ou partagez ce QR code pour accéder à la page publique du chantier.
          </p>
          {qr ? (
            <>
              <img src={qr.qrCode} alt="QR Code" />
              <p style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                <a href={qr.url} target="_blank" rel="noreferrer">{qr.url}</a>
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                <a href={qr.qrCode} download={`qr-${project.slug}.png`} className="btn btn-primary">
                  <Download size={16} /> Télécharger PNG
                </a>
                <button className="btn" onClick={() => { navigator.clipboard.writeText(qr.url); toast.success('Lien copié'); }}>
                  Copier le lien
                </button>
              </div>
            </>
          ) : (
            <p>Chargement du QR code...</p>
          )}
        </div>
      )}

      {tab === 'suivi' && (
        <SignatureTools project={project} onProjectChange={load} />
      )}

      {tab === 'settings' && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>État du chantier</h3>
          <div className="toggle-row" style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '2px solid var(--border)' }}>
            <div>
              <span style={{ fontWeight: 600 }}>Chantier terminé</span>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: 2 }}>
                Le chantier sera déplacé dans la section "Chantiers terminés" du tableau de bord.
              </p>
            </div>
            <button
              className={`toggle ${project.status === 'COMPLETED' ? 'active' : ''}`}
              onClick={toggleCompleted}
            />
          </div>

          <h3 style={{ marginBottom: 16 }}>Page publique (QR Code)</h3>
          <div className="toggle-row" style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
            <div>
              <span style={{ fontWeight: 600 }}>Accès public activé</span>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: 2 }}>
                Le QR code restera valide tant que cette option est activée. Vous pouvez l'imprimer en toute sécurité.
              </p>
            </div>
            <button className={`toggle ${form.publicAccessEnabled ? 'active' : ''}`}
              onClick={() => setForm({ ...form, publicAccessEnabled: !form.publicAccessEnabled })} />
          </div>
          <div className="toggle-row" style={{ alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Home size={15} /> Mettre en avant sur la page d'accueil</span>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: 2 }}>
                Ce chantier et sa photo de couverture ouvrent votre vitrine. Un seul chantier à la fois : il remplace le précédent.
              </p>
            </div>
            <button className={`toggle ${project.featured ? 'active' : ''}`}
              aria-pressed={!!project.featured}
              onClick={() => setFeatured(!project.featured)} />
          </div>
          <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: 16 }}>
            Choisissez quelles informations sont visibles par les visiteurs.
          </p>
          {[
            { key: 'showProgress', label: 'Afficher l\'avancement dans la liste des chantiers' },
            { key: 'showPhotos', label: 'Afficher les photos' },
            { key: 'showComments', label: 'Afficher les commentaires' },
            { key: 'showDates', label: 'Afficher les dates' },
          ].map(({ key, label }) => (
            <div className="toggle-row" key={key}>
              <span>{label}</span>
              <button className={`toggle ${form[key] ? 'active' : ''}`}
                onClick={() => setForm({ ...form, [key]: !form[key] })} />
            </div>
          ))}
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={save}>
            <Save size={16} /> Sauvegarder
          </button>
        </div>
      )}
    </div>
  );
}
